import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { computeHpp, fmtBulan, fmtPersen, sumRows } from "@/lib/hpp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package, Layers, Tag, Timer, Landmark, CalendarClock, Boxes, Target, Plus, Trash2, ChevronDown,
  Save, Download, FileSpreadsheet, Copy, CheckCircle2, AlertTriangle, RotateCcw, ArrowLeft,
} from "lucide-react";

const newRow = () => ({ nama: "", nilai: 0, _k: Math.random().toString(36).slice(2) });
const withKeys = (rows) => (rows && rows.length ? rows.map((r) => ({ ...r, _k: Math.random().toString(36).slice(2) })) : [newRow()]);
const EMPTY = { nama: "", deskripsi: "", kategori_id: "", kategori_nama: "", margin_persen: 0, harga_aktual: null, volume: 0, capex: [newRow()], opex: [newRow()], variabel: [newRow()] };

const Metric = ({ icon: Icon, label, value, sub, tone = "text-slate-900", testid }) => (
  <div className="bg-white rounded-md shadow-sm border border-slate-100 p-3 md:p-4 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-wide text-slate-500"><Icon size={13} className="brand-text shrink-0" /><span className="truncate">{label}</span></div>
    <div className={`mt-1 font-mono text-sm sm:text-base md:text-xl font-semibold tabular-nums truncate ${tone}`}>{value}</div>
    {sub && <div className="text-[10px] md:text-[11px] text-slate-400 truncate">{sub}</div>}
  </div>
);

function Section({ id, icon: Icon, title, hint, total, totalLabel, rows, setRows, namePh, valuePh, open, setOpen }) {
  const isOpen = open[id];
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="bg-white rounded-md shadow-sm border border-slate-100 overflow-hidden" data-testid={`hpp-sec-${id}`}>
      <button type="button" onClick={() => setOpen({ ...open, [id]: !isOpen })} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`hpp-sec-toggle-${id}`}>
        <div className="h-9 w-9 rounded-sm bg-blue-50 flex items-center justify-center shrink-0"><Icon size={18} className="brand-text" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 text-sm md:text-base">{title}</div>
          <div className="text-[11px] text-slate-500 truncate">{hint}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] text-slate-400">{totalLabel}</div>
          <div className="font-mono text-sm font-semibold" data-testid={`hpp-sec-total-${id}`}>{rupiah(total)}</div>
        </div>
        <ChevronDown size={18} className={`text-slate-400 transition-transform shrink-0 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="px-4 pb-4 space-y-2.5 border-t border-slate-100 pt-3">
          {rows.map((r, i) => (
            <div key={r._k} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_200px_auto] gap-2 items-center" data-testid={`hpp-row-${id}-${i}`}>
              <Input value={r.nama} onChange={(e) => upd(i, "nama", e.target.value)} placeholder={namePh} className="h-11 md:h-10 col-span-1" data-testid={`hpp-row-${id}-${i}-nama`} />
              <div className="col-span-1 row-start-2 sm:row-start-auto">
                <CurrencyInput value={r.nilai} onValueChange={(v) => upd(i, "nilai", v)} placeholder={valuePh} className="h-11 md:h-10" data-testid={`hpp-row-${id}-${i}-nilai`} />
              </div>
              <button type="button" onClick={() => setRows(rows.length > 1 ? rows.filter((_, j) => j !== i) : [newRow()])}
                className="h-11 w-11 md:h-10 md:w-10 flex items-center justify-center rounded-sm text-rose-600 hover:bg-rose-50 row-span-2 sm:row-span-1" aria-label="Hapus baris" data-testid={`hpp-row-${id}-${i}-del`}>
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setRows([...rows, newRow()])} className="w-full sm:w-auto h-11 md:h-9 rounded-sm border-dashed" data-testid={`hpp-add-${id}`}>
            <Plus size={16} className="mr-1.5" /> Add Row
          </Button>
        </div>
      )}
    </div>
  );
}

export default function HppCalculator() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);
  const [open, setOpen] = useState({ capex: true, opex: true, variabel: true });

  useEffect(() => {
    api.get("/hpp/categories").then((r) => {
      setCats(r.data);
      if (!id && r.data[0]) setF((x) => ({ ...x, kategori_id: r.data[0].id, kategori_nama: r.data[0].nama, margin_persen: r.data[0].margin_persen }));
    }).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => {
    if (!id) { setF((x) => ({ ...EMPTY, kategori_id: x.kategori_id, kategori_nama: x.kategori_nama, margin_persen: x.margin_persen })); setLoaded(true); return; }
    setLoaded(false);
    api.get(`/hpp/simulations/${id}`).then((r) => {
      const d = r.data;
      setF({ ...EMPTY, ...d, kategori_id: d.kategori_id || "custom", capex: withKeys(d.capex), opex: withKeys(d.opex), variabel: withKeys(d.variabel) });
      setLoaded(true);
    }).catch((e) => { toast.error(formatApiError(e)); nav("/hpp"); });
  }, [id, nav]);

  const h = useMemo(() => computeHpp(f), [f]);
  const isCustom = f.kategori_id === "custom";

  const pickKategori = (v) => {
    if (v === "custom") { setF({ ...f, kategori_id: "custom", kategori_nama: "Custom" }); return; }
    const c = cats.find((x) => x.id === v);
    if (c) setF({ ...f, kategori_id: c.id, kategori_nama: c.nama, margin_persen: c.margin_persen });
  };

  const payload = () => ({
    nama: f.nama, deskripsi: f.deskripsi || "", kategori_id: isCustom ? null : f.kategori_id || null,
    kategori_nama: isCustom ? "Custom" : f.kategori_nama, margin_persen: Number(f.margin_persen) || 0,
    harga_aktual: h.hasAktual ? Number(f.harga_aktual) || 0 : null, volume: Number(f.volume) || 0,
    capex: f.capex.map(({ nama, nilai }) => ({ nama, nilai: Number(nilai) || 0 })),
    opex: f.opex.map(({ nama, nilai }) => ({ nama, nilai: Number(nilai) || 0 })),
    variabel: f.variabel.map(({ nama, nilai }) => ({ nama, nilai: Number(nilai) || 0 })),
  });

  const save = async () => {
    if (!f.nama.trim()) { toast.error("Nama simulasi wajib diisi"); return; }
    setSaving(true);
    try {
      if (id) { await api.put(`/hpp/simulations/${id}`, payload()); toast.success("Simulasi disimpan"); }
      else { const r = await api.post("/hpp/simulations", payload()); toast.success("Simulasi disimpan"); nav(`/hpp/${r.data.id}`, { replace: true }); }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const duplicate = async () => {
    try { const r = await api.post(`/hpp/simulations/${id}/duplicate`); toast.success("Simulasi diduplikat"); nav(`/hpp/${r.data.id}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const download = async (fmt) => {
    try {
      await api.put(`/hpp/simulations/${id}`, payload());
      const res = await fetch(`${API_BASE}/hpp/simulations/${id}/export?fmt=${fmt}`, { headers: { Authorization: `Bearer ${localStorage.getItem("khn_token")}` } });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const a = document.createElement("a"); a.href = URL.createObjectURL(await res.blob());
      a.download = `simulasi-hpp-${(f.nama || "hpp").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
    } catch (e) { toast.error(e.message || "Gagal mengunduh"); }
  };

  if (!loaded) return <div className="p-8 text-slate-400 text-sm">Memuat simulasi...</div>;

  const priceBad = h.hppTotal > 0 && h.harga <= h.hppTotal;
  return (
    <div className="pb-24 md:pb-8">
      {/* Live Summary Sticky Banner */}
      <div className="sticky top-0 z-30 bg-[var(--bg)]/95 backdrop-blur border-b border-slate-200/70 px-3 md:px-8 py-2.5 md:py-3" data-testid="hpp-summary-banner">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
          <Metric icon={Package} label="HPP Dasar / Unit" value={rupiah(h.hppDasar)} sub="Biaya variabel" testid="hpp-m-dasar" />
          <Metric icon={Layers} label="HPP Total / Unit" value={rupiah(h.hppTotal)} sub={h.volume > 0 ? "Termasuk operasional" : "Isi target volume"} testid="hpp-m-total" />
          <Metric icon={Tag} label="Rekomendasi Harga" value={rupiah(h.rekomendasi)} sub={`Margin ${fmtPersen(f.margin_persen)}`} tone="brand-text" testid="hpp-m-harga" />
          <Metric icon={Timer} label="Estimasi BEP" value={h.bep === null ? "—" : fmtBulan(h.bep)} sub={h.bep === null ? "Belum bisa dihitung" : "Balik modal"} tone={h.bep === null ? "text-slate-400" : "text-emerald-700"} testid="hpp-m-bep" />
        </div>
      </div>

      <div className="px-3 md:px-8 pt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-4 md:gap-5">
        <div className="space-y-4 order-2 lg:order-1">
          <Section id="capex" icon={Landmark} title="Modal Awal / Investasi (Capex)" hint="Mesin, renovasi, peralatan, perizinan" total={h.totalModal} totalLabel="Total Modal"
            rows={f.capex} setRows={(rows) => setF({ ...f, capex: rows })} namePh="Nama item" valuePh="Total harga" open={open} setOpen={setOpen} />
          <Section id="opex" icon={CalendarClock} title="Biaya Tetap Bulanan (Fixed Opex)" hint="Gaji, sewa, listrik, internet, marketing" total={h.totalOpex} totalLabel="Total Operasional"
            rows={f.opex} setRows={(rows) => setF({ ...f, opex: rows })} namePh="Nama pengeluaran" valuePh="Biaya per bulan" open={open} setOpen={setOpen} />
          <Section id="variabel" icon={Boxes} title="Biaya Variabel (Bahan Baku) / Unit" hint="Biaya material per 1 unit produk jadi" total={sumRows(f.variabel)} totalLabel="HPP Dasar"
            rows={f.variabel} setRows={(rows) => setF({ ...f, variabel: rows })} namePh="Nama material" valuePh="Biaya per 1 unit" open={open} setOpen={setOpen} />
          <div className="bg-white rounded-md shadow-sm border border-slate-100 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="hpp-sec-volume">
            <div className="flex items-center gap-3 flex-1">
              <div className="h-9 w-9 rounded-sm bg-blue-50 flex items-center justify-center shrink-0"><Target size={18} className="brand-text" /></div>
              <div><div className="font-semibold text-slate-900 text-sm md:text-base">Target Volume Penjualan / Produksi</div><div className="text-[11px] text-slate-500">Asumsi unit terjual per bulan</div></div>
            </div>
            <div className="flex items-center gap-2 sm:w-56">
              <Input type="number" inputMode="numeric" min="0" value={f.volume || ""} onChange={(e) => setF({ ...f, volume: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })} placeholder="0" className="h-11 md:h-10 text-right font-mono" data-testid="hpp-volume" />
              <span className="text-sm text-slate-500">unit</span>
            </div>
          </div>
        </div>

        <div className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-[132px] self-start">
          <div className="bg-white rounded-md shadow-sm border border-slate-100 p-4 space-y-3" data-enter-scope>
            <div className="space-y-1.5"><Label>Nama Simulasi <span className="text-rose-500">*</span></Label>
              <Input value={f.nama} onChange={(e) => setF({ ...f, nama: e.target.value })} placeholder="mis. Briket Ekspor 2027" className="h-11 md:h-10" data-testid="hpp-nama" /></div>
            <div className="space-y-1.5"><Label>Kategori Industri</Label>
              <Select value={f.kategori_id || ""} onValueChange={pickKategori}>
                <SelectTrigger className="h-11 md:h-10" data-testid="hpp-kategori"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.nama} (Margin {Number(c.margin_persen).toLocaleString("id-ID")}%)</SelectItem>)}
                  <SelectItem value="custom">Custom (isi % sendiri)</SelectItem>
                </SelectContent>
              </Select>
              {isCustom && (
                <div className="flex items-center gap-2 pt-1">
                  <Input type="number" inputMode="decimal" min="0" value={f.margin_persen} onChange={(e) => setF({ ...f, margin_persen: Math.max(0, Number(e.target.value) || 0) })} className="h-11 md:h-10 font-mono" data-testid="hpp-margin-custom" />
                  <span className="text-sm text-slate-500">% margin</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Simulasi Harga Jual Aktual</Label>
                {h.hasAktual && <button type="button" onClick={() => setF({ ...f, harga_aktual: null })} className="text-[11px] brand-text inline-flex items-center gap-1" data-testid="hpp-reset-harga"><RotateCcw size={12} /> Ikuti rekomendasi</button>}
              </div>
              <CurrencyInput value={h.hasAktual ? f.harga_aktual : Math.round(h.rekomendasi)} onValueChange={(v) => setF({ ...f, harga_aktual: v })} className="h-11 md:h-10 font-mono" data-testid="hpp-harga-aktual" />
              {h.hppTotal > 0 && (priceBad ? (
                <div className="flex gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-2.5" data-testid="hpp-price-msg">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Harga di bawah / sama dengan HPP — rugi {rupiah(Math.abs(h.labaUnit))} per unit, modal tidak akan kembali.</span>
                </div>
              ) : (
                <div className="flex gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm p-2.5" data-testid="hpp-price-msg">
                  <CheckCircle2 size={14} className="shrink-0 mt-0.5" />
                  <span>Pada harga ini, Margin Anda menjadi <b>{fmtPersen(h.marginAktual)}</b> dan Modal awal kembali dalam <b>{h.bepAktual === null ? "— (isi target volume)" : fmtBulan(h.bepAktual)}</b>.</span>
                </div>
              ))}
              {h.volume > 0 && !priceBad && h.hppTotal > 0 && (
                <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
                  <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">Omzet / bulan</div><div className="font-mono font-semibold">{rupiah(h.omzetBulanan)}</div></div>
                  <div className="bg-slate-50 rounded-sm p-2"><div className="text-slate-500">Laba kotor / bulan</div><div className="font-mono font-semibold text-emerald-700">{rupiah(h.labaBulanan)}</div></div>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Catatan (opsional)</Label>
              <Textarea value={f.deskripsi || ""} onChange={(e) => setF({ ...f, deskripsi: e.target.value })} rows={2} data-testid="hpp-deskripsi" /></div>
            <div className="hidden md:flex flex-wrap gap-2 pt-1">
              <Button onClick={save} disabled={saving} className="brand-btn rounded-sm" data-enter-submit data-testid="hpp-save"><Save size={15} className="mr-1.5" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
              {id && <>
                <Button variant="outline" onClick={() => download("pdf")} className="rounded-sm" data-testid="hpp-pdf"><Download size={15} className="mr-1" /> PDF</Button>
                <Button variant="outline" onClick={() => download("xlsx")} className="rounded-sm" data-testid="hpp-xlsx"><FileSpreadsheet size={15} className="mr-1" /> Excel</Button>
                <Button variant="outline" onClick={duplicate} className="rounded-sm" data-testid="hpp-duplicate"><Copy size={15} className="mr-1" /> Duplikat</Button>
              </>}
            </div>
          </div>
          <button onClick={() => nav("/hpp")} className="hidden lg:inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Kembali ke daftar simulasi</button>
        </div>
      </div>

      {/* Mobile action bar (di atas bottom nav) */}
      <div className="md:hidden fixed bottom-[60px] inset-x-0 z-30 bg-white border-t border-slate-200 p-2.5 flex gap-2">
        <Button onClick={save} disabled={saving} className="brand-btn rounded-sm h-11 flex-1" data-testid="hpp-save-m"><Save size={16} className="mr-1.5" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
        {id && <>
          <Button variant="outline" onClick={() => download("pdf")} className="rounded-sm h-11 px-3" aria-label="PDF"><Download size={16} /></Button>
          <Button variant="outline" onClick={() => download("xlsx")} className="rounded-sm h-11 px-3" aria-label="Excel"><FileSpreadsheet size={16} /></Button>
          <Button variant="outline" onClick={duplicate} className="rounded-sm h-11 px-3" aria-label="Duplikat"><Copy size={16} /></Button>
        </>}
      </div>
    </div>
  );
}
